import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, Chip, CircularProgress,
    Accordion, AccordionSummary, AccordionDetails,
    Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper,
    IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Alert,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    PersonAdd as PersonAddIcon,
    Refresh as ResendIcon,
    Cancel as CancelIcon,
} from '@mui/icons-material';
import { driversAPI } from '../services/apiService';
import signalRService from '../services/signalRService';
import PendingDriverDetailModal from './PendingDriverDetailModal';
import { useAlert } from '../contexts/AlertContext';
import { formatDate } from '../utils/dateHelpers';

const STATUS_COLORS = {
    submitted: 'warning',
    invited: 'info',
    rejected: 'error',
};

export default function DriverApplicationsView() {
    const { showAlert, showToast } = useAlert();
    const [data, setData] = useState({ submitted: [], invited: [], rejected: [] });
    const [loading, setLoading] = useState(true);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteEmailError, setInviteEmailError] = useState('');
    const [inviteLoading, setInviteLoading] = useState(false);
    const [detailId, setDetailId] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const result = await driversAPI.getApplications();
            setData(result || { submitted: [], invited: [], rejected: [] });
        } catch (err) {
            showToast('Failed to load applications', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const unsub = signalRService.onDriverApplicationSubmitted(() => load());
        return unsub;
    }, [load]);

    const validateInviteEmail = (email) => {
        if (!email.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'Please enter a valid email address';
        return '';
    };

    const handleInvite = async () => {
        const error = validateInviteEmail(inviteEmail);
        if (error) { setInviteEmailError(error); return; }
        setInviteLoading(true);
        try {
            await driversAPI.invite(inviteEmail.trim());
            showToast('Invitation sent!', 'success');
            setInviteOpen(false);
            setInviteEmail('');
            setInviteEmailError('');
            load();
        } catch (err) {
            const msg = err?.response?.data?.error || 'Failed to send invite';
            showToast(msg, 'error');
        } finally {
            setInviteLoading(false);
        }
    };

    const handleResend = async (id) => {
        try {
            await driversAPI.resendInvite(id);
            showToast('Invite resent', 'success');
            load();
        } catch {
            showToast('Failed to resend invite', 'error');
        }
    };

    const handleCancel = (id) => {
        showAlert('Cancel Invitation', 'Are you sure you want to cancel this invitation? The driver record will be deleted.', [
            { label: 'Keep', color: 'inherit' },
            {
                label: 'Cancel Invite', color: 'error', onClick: async () => {
                    try {
                        await driversAPI.cancelInvite(id);
                        showToast('Invitation cancelled', 'success');
                        load();
                    } catch {
                        showToast('Failed to cancel invite', 'error');
                    }
                }
            },
        ]);
    };

    const openDetail = (id) => {
        setDetailId(id);
        setDetailOpen(true);
    };

    const Section = ({ title, color, rows, columns, onRowClick, showActions = true }) => (
        <Accordion defaultExpanded={color === 'warning'} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography fontWeight={600}>{title}</Typography>
                    <Chip label={rows.length} color={color} size="small" />
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
                {rows.length === 0 ? (
                    <Typography color="text.secondary" sx={{ p: 2 }}>No entries.</Typography>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {columns.map(c => <TableCell key={c.key} sx={{ fontWeight: 600 }}>{c.label}</TableCell>)}
                                    {showActions && <TableCell />}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map(row => (
                                    <TableRow
                                        key={row.id}
                                        hover
                                        sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                                    >
                                        {columns.map(c => (
                                            <TableCell key={c.key}>{c.render ? c.render(row) : (row[c.key] ?? '—')}</TableCell>
                                        ))}
                                        {showActions && (
                                            <TableCell align="right" onClick={e => e.stopPropagation()}>
                                                {row._actions}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </AccordionDetails>
        </Accordion>
    );

    const submittedRows = data.submitted?.map(d => ({ ...d }));

    const invitedRows = data.invited?.map(d => ({
        ...d,
        _actions: (
            <Box display="flex" justifyContent="flex-end">
                <Tooltip title="Resend invite"><IconButton size="small" onClick={() => handleResend(d.id)}><ResendIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Cancel invite"><IconButton size="small" color="error" onClick={() => handleCancel(d.id)}><CancelIcon fontSize="small" /></IconButton></Tooltip>
            </Box>
        )
    }));

    const rejectedRows = data.rejected?.map(d => ({ ...d }));

    if (loading) return <Box p={4} display="flex" justifyContent="center"><CircularProgress /></Box>;

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={700}>Driver Applications</Typography>
                <Button startIcon={<PersonAddIcon />} variant="contained" onClick={() => setInviteOpen(true)}>
                    Invite New Driver
                </Button>
            </Box>

            <Section
                title="Submitted Applications"
                color="warning"
                rows={submittedRows}
                columns={[
                    { key: 'name', label: 'Name' },
                    { key: 'email', label: 'Email' },
                    { key: 'phoneNumber', label: 'Phone' },
                    { key: 'applicationSubmittedUtc', label: 'Submitted', render: r => formatDate(r.applicationSubmittedUtc) },
                ]}
                onRowClick={row => openDetail(row.id)}
                showActions={false}
            />

            <Section
                title="Pending Invites"
                color="info"
                rows={invitedRows}
                columns={[
                    { key: 'email', label: 'Email' },
                    { key: 'applicationTokenExpiry', label: 'Expires', render: r => formatDate(r.applicationTokenExpiry) },
                ]}
            />

            <Section
                title="Rejected"
                color="error"
                rows={rejectedRows}
                columns={[
                    { key: 'name', label: 'Name' },
                    { key: 'email', label: 'Email' },
                    { key: 'rejectionReason', label: 'Reason' },
                    { key: 'rejectedUtc', label: 'Rejected', render: r => formatDate(r.rejectedUtc) },
                ]}
                onRowClick={row => openDetail(row.id)}
                showActions={false}
            />

            {/* Invite dialog */}
            <Dialog open={inviteOpen} onClose={() => { setInviteOpen(false); setInviteEmail(''); setInviteEmailError(''); }} maxWidth="xs" fullWidth>
                <DialogTitle>Invite New Driver</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Enter the driver's email address. They'll receive a link to complete their application.
                    </Typography>
                    <TextField
                        label="Email address"
                        type="email"
                        value={inviteEmail}
                        onChange={e => { setInviteEmail(e.target.value); if (inviteEmailError) setInviteEmailError(''); }}
                        error={!!inviteEmailError}
                        helperText={inviteEmailError}
                        fullWidth
                        autoFocus
                        onKeyDown={e => e.key === 'Enter' && handleInvite()}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setInviteOpen(false); setInviteEmail(''); setInviteEmailError(''); }}>Cancel</Button>
                    <Button onClick={handleInvite} variant="contained" disabled={inviteLoading}>
                        {inviteLoading ? <CircularProgress size={18} /> : 'Send Invite'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Detail modal */}
            {detailOpen && detailId && (
                <PendingDriverDetailModal
                    driverId={detailId}
                    open={detailOpen}
                    onClose={() => setDetailOpen(false)}
                    onAction={() => { setDetailOpen(false); load(); }}
                />
            )}
        </Box>
    );
}
